import { inject, Injectable } from '@angular/core';
import { map } from 'rxjs';
import { ConstructAPI } from 'src/app/API/constructAPI';
import { RestApiService } from 'src/app/API/restapi';
import { Docuseal } from 'src/model/formJ.model';

@Injectable({
	providedIn: 'root'
})
export class DocusealAdapter {
	adapt(item: any): Docuseal {
		return {
			id: item.id,
			expireAt: item.expireAt,
			openedAt: item.openedAt,
			createdAt: item.createdOn,
			updatedAt: item.updatedAt,
			completedAt: item.completedAt,
			status: item.status,
			documentsUrl: item.documentsUrl,
			customerLink: item.customerLink,
			contactLink: item.contactLink,
			link: item.link
		};
	}
}

@Injectable({
	providedIn: 'root'
})
export class DocusealService {
	private docusealAdapter = inject(DocusealAdapter);
	private api = inject(RestApiService);
	private constructApi = inject(ConstructAPI);

	//#region 'FORM J DOCUSEALS'
	getPostFormJDocuseal(formJId: number, omitDate?: boolean) {
		return new Promise<boolean>((resolve, reject) => {
			this.api.post(this.constructApi.getFormJDocuseal(formJId, omitDate)).subscribe({
				next: () => resolve(true),
				error: error => {
					this.api.handleError(error);
					reject(error);
				}
			});
		});
	}

	deleteFormJDocuseal(id: number) {
		return new Promise<boolean>((resolve, reject) => {
			this.api.delete(this.constructApi.getFormJDocuseal(id)).subscribe({
				next: () => resolve(true),
				error: error => {
					this.api.handleError(error);
					reject(error);
				}
			});
		});
	}

	getFormJDocusealDetails(id: number): Promise<Docuseal> {
		return new Promise<Docuseal>((resolve, reject) => {
			this.api
				.get<Docuseal>(this.constructApi.getFormJDocuseal(id))
				.pipe(map(data => this.docusealAdapter.adapt(data)))
				.subscribe({
					next: data => {
						resolve(data);
					},
					error: error => {
						this.api.handleError(error);
						reject(error);
					}
				});
		});
	}

	getCustomerFormJDocuseals(customerId: number): Promise<Docuseal[]> {
		return new Promise<Docuseal[]>((resolve, reject) => {
			this.api
				.get<Docuseal[]>(this.constructApi.getCustomerFormJDocuseals(customerId))
				.pipe(map(data => data.map(d => this.docusealAdapter.adapt(d))))
				.subscribe({
					next: data => resolve(data),
					error: error => {
						this.api.handleError(error);
						reject(error);
					}
				});
		});
	}

	//#endregion

	//#region 'GUARANTOR DOCUSEALS'
	getPostGuarantorDocuseal(customerId: number, contactId: number, date: Date, amount: Number) {
		return new Promise<Docuseal>((resolve, reject) => {
			this.api.post(this.constructApi.getPostGuarantorDocuseal(customerId, contactId), { date: date, amount: amount }).subscribe({
				next: response => {
					resolve(this.docusealAdapter.adapt(response));
				},
				error: error => {
					this.api.handleError(error);
					reject(error);
				}
			});
		});
	}

	deleteGuarantorDocuseal(customerId: number, contactId: number, id: number) {
		return new Promise<boolean>((resolve, reject) => {
			this.api.delete(this.constructApi.getGuarantorDocusealDetails(customerId, contactId, id)).subscribe({
				next: () => resolve(true),
				error: error => {
					this.api.handleError(error);
					reject(error);
				}
			});
		});
	}

	getGuarantorDocusealDetails(customerId: number, contactId: number, id: number): Promise<Docuseal> {
		return new Promise<Docuseal>((resolve, reject) => {
			this.api
				.get<Docuseal>(this.constructApi.getGuarantorDocusealDetails(customerId, contactId, id))
				.pipe(map(data => this.docusealAdapter.adapt(data)))
				.subscribe({
					next: data => {
						resolve(data);
					},
					error: error => {
						this.api.handleError(error);
						reject(error);
					}
				});
		});
	}

	getGuarantorDocuseals(customerId: number, contactId: number): Promise<Docuseal[]> {
		return new Promise<Docuseal[]>((resolve, reject) => {
			this.api
				.get<Docuseal[]>(this.constructApi.getCustomerGuarantorDocuseals(customerId, contactId))
				.pipe(map(data => data.map(d => this.docusealAdapter.adapt(d))))
				.subscribe({
					next: data => resolve(data),
					error: error => {
						this.api.handleError(error);
						reject(error);
					}
				});
		});
	}
	//#endregion

	//#region 'SETTLEMENT BREAKDOWN DOCUSEALS'
	getPostSettlementBreakdownDocuseal(customerId: number, data: any) {
		return new Promise<Docuseal>((resolve, reject) => {
			this.api.post(this.constructApi.getPostSettlementBreakdownDocuseal(customerId), data).subscribe({
				next: response => {
					resolve(this.docusealAdapter.adapt(response));
				},
				error: error => {
					this.api.handleError(error);
					reject(error);
				}
			});
		});
	}
	//#endregion

	//#region 'ACCOUNT AUTHORIZATION LETTER'
	getPostAccountAuthorizationDocuseal(customerId: number, data: any) {
		return new Promise<Docuseal>((resolve, reject) => {
			this.api.post(this.constructApi.getPostAccountAuthorizationDocuseal(customerId), data).subscribe({
				next: response => {
					resolve(this.docusealAdapter.adapt(response));
				},
				error: error => {
					this.api.handleError(error);
					reject(error);
				}
			});
		});
	}
	//#endregion

	//#region 'CUSTOMER DOCUSEALS'
	getCustomerDocuseals(customerId: number, type?: string): Promise<Docuseal[]> {
		return new Promise<Docuseal[]>((resolve, reject) => {
			this.api
				.get<Docuseal[]>(this.constructApi.getCustomerDocuseals(customerId, type))
				.pipe(map(data => data.map(d => this.docusealAdapter.adapt(d))))
				.subscribe({
					next: data => resolve(data),
					error: error => {
						this.api.handleError(error);
						reject(error);
					}
				});
		});
	}

	getCustomerDocusealDetails(customerId: number, id: number): Promise<Docuseal> {
		return new Promise<Docuseal>((resolve, reject) => {
			this.api
				.get<Docuseal>(this.constructApi.getCustomerDocusealDetails(customerId, id))
				.pipe(map(data => this.docusealAdapter.adapt(data)))
				.subscribe({
					next: data => {
						resolve(data);
					},
					error: error => {
						this.api.handleError(error);
						reject(error);
					}
				});
		});
	}

	deleteCustomerDocuseal(customerId: number, id: number) {
		return new Promise<boolean>((resolve, reject) => {
			this.api.delete(this.constructApi.getCustomerDocusealDetails(customerId, id)).subscribe({
				next: () => resolve(true),
				error: error => {
					this.api.handleError(error);
					reject(error);
				}
			});
		});
	}

	//#endregion
}
